/**
 * Explicit published identities, independent of the large server content registry.
 * Client navigation, route handlers, sitemaps and redirects share this module.
 * Never infer publication or equivalence from a neighbourhood name alone.
 * Alias evidence and unresolved scopes: docs/seo/service-area-route-decisions.csv.
 */
export type NeighbourhoodRoute = {
  key: string
  city: string
  neighbourhood: string
  href: string
}

const PRIMARY_NEIGHBOURHOOD_KEYS = [
  'toronto/rosedale',
  'toronto/forest-hill',
  'toronto/lawrence-park',
  'toronto/hoggs-hollow',
  'toronto/the-bridle-path',
  'toronto/moore-park',
  'toronto/summerhill',
  'toronto/yorkville',
  'toronto/the-annex',
  'toronto/cabbagetown',
  'toronto/casa-loma',
  'toronto/lytton-park',
  'toronto/wychwood-park',
  'toronto/leaside',
  'toronto/lawrence-manor',
  'toronto/deer-park',
  'toronto/south-hill',
  'toronto/davisville-village',
  'toronto/riverdale',
  'toronto/leslieville',
  'toronto/the-beaches',
  'toronto/greektown',
  'toronto/pape-village',
  'toronto/corktown',
  'toronto/distillery-district',
  'toronto/regent-park',
  'toronto/st-lawrence',
  'toronto/cherry-beach',
  'toronto/high-park',
  'toronto/swansea',
  'toronto/baby-point',
  'toronto/roncesvalles',
  'toronto/parkdale',
  'toronto/bloor-west-village',
  'toronto/the-junction',
  'toronto/junction-triangle',
  'toronto/dovercourt',
  'toronto/liberty-village',
  'toronto/king-west',
  'toronto/queen-west',
  'toronto/west-queen-west',
  'toronto/trinity-bellwoods',
  'toronto/ossington',
  'toronto/dundas-west',
  'toronto/little-italy',
  'toronto/little-portugal',
  'toronto/bloordale',
  'toronto/kensington-market',
  'toronto/chinatown',
  'toronto/downtown',
  'toronto/entertainment-district',
  'toronto/financial-district',
  'toronto/bay-street-corridor',
  'toronto/cityplace',
  'toronto/old-town',
  'toronto/mimico',
  'toronto/new-toronto',
  'toronto/long-branch',
  'toronto/humber-bay-shores',
  'toronto/the-bluffs',
  'toronto/guildwood',
  'toronto/birchcliff',
  'toronto/cliffside',
  'toronto/agincourt',
  'toronto/willowdale',
  'toronto/don-mills',
  'toronto/bayview-village',
  'toronto/york-mills',
  'thornhill/vaughan-grove',
  'thornhill/pomona-mills',
  'thornhill/henderson',
  'thornhill/uplands',
  'thornhill/commerce-valley',
  'thornhill/thornhill-woods',
  'thornhill/royal-orchard',
  'thornhill/old-thornhill-village',
  'thornhill/beverley-glen',
  'thornhill/german-mills',
  'thornhill/patterson',
  'markham/unionville',
  'markham/cathedraltown',
  'markham/bayview-hill',
  'markham/bayview-glen',
  'markham/mill-pond',
  'markham/cornell',
  'markham/berczy-village',
  'markham/victoria-square',
  'markham/wismer',
  'markham/swan-lake',
  'markham/box-grove',
  'markham/greensborough',
  'markham/markham-village',
  'markham/milliken',
  'markham/markville',
  'markham/buttonville',
  'markham/angus-glen',
  'markham/cedarwood',
  'richmond-hill/oak-ridges',
  'richmond-hill/jefferson',
  'richmond-hill/mill-pond',
  'richmond-hill/crosby',
  'vaughan/woodbridge',
  'vaughan/kleinburg',
  'vaughan/maple',
  'vaughan/concord',
  'mississauga/port-credit',
  'mississauga/lorne-park',
  'mississauga/mineola',
  'mississauga/cooksville',
  'mississauga/erin-mills',
  'mississauga/meadowvale',
  'mississauga/streetsville',
  'mississauga/malton',
  'mississauga/clarkson',
  'mississauga/lakeview',
  'mississauga/mississauga-valleys',
  'mississauga/hurontario',
  'mississauga/square-one',
  'mississauga/city-centre',
  'mississauga/sheridan',
  'mississauga/sherwood-forrest',
  'mississauga/churchill-meadows',
  'mississauga/central-erin-mills',
  'mississauga/rathwood',
  'mississauga/applewood',
  'mississauga/dixie',
  'mississauga/mississauga-road-corridor',
  'mississauga/credit-valley',
  'mississauga/east-credit',
  'oakville/glen-abbey',
  'oakville/bronte',
  'oakville/old-oakville',
  'oakville/clearview',
  'oakville/river-oaks',
  'oakville/west-oak-trails',
  'oakville/palermo',
  'oakville/iroquois-ridge',
  'oakville/joshua-creek',
  'oakville/bronte-creek',
  'oakville/college-park',
  'oakville/eastlake',
  'oakville/falgarwood',
  'oakville/morrison',
  'oakville/westmount',
  'oakville/uptown-core',
  'oakville/glenorchy',
  'oakville/preserve',
  'burlington/aldershot',
  'burlington/brant-hills',
  'burlington/tyandaga',
  'brampton/bramalea',
  'brampton/heart-lake',
  'brampton/springdale',
  'caledon/bolton',
  'caledon/palgrave',
  'caledon/caledon-village',
  'durham/pickering',
  'durham/ajax',
  'durham/whitby',
  'durham/oshawa',
  'durham/bowmanville',
  'durham/port-perry',
  'pickering/bay-ridges',
  'pickering/dunbarton',
  'pickering/liverpool',
  'ajax/pickering-village',
  'ajax/central-west',
  'ajax/south-west',
  'whitby/port-whitby',
  'whitby/brooklin',
  'whitby/pringle-creek',
  'whitby/williamsburg',
  'oshawa/lakeview',
  'oshawa/northglen',
  'oshawa/samac',
  'cottage-country/muskoka-lakes',
  'cottage-country/bracebridge',
  'cottage-country/huntsville',
  'cottage-country/port-carling',
  'cottage-country/collingwood',
  'cottage-country/blue-mountain',
  'cottage-country/parry-sound',
  'cottage-country/haliburton',
  'cottage-country/prince-edward-county',
  'cottage-country/honey-harbour',
  'cottage-country/thornbury',
  'cottage-country/lake-joseph',
  'cottage-country/lake-rosseau',
  'muskoka/bracebridge',
  'muskoka/huntsville',
  'muskoka/gravenhurst',
  'muskoka/port-carling',
  'muskoka/bala',
  'muskoka/lake-joseph',
  'muskoka/lake-rosseau',
  'collingwood/blue-mountain',
  'collingwood/thornbury',
  'blue-mountain/thornbury',
  'prince-edward-county/picton',
  'prince-edward-county/wellington',
  'prince-edward-county/bloomfield',
] as const

export const SERVICE_AREA_ALIASES: Readonly<Record<string, string>> = {
  'york/unionville': 'markham/unionville',
  'york/kleinburg': 'vaughan/kleinburg',
  'york/woodbridge': 'vaughan/woodbridge',
  'york/oak-ridges': 'richmond-hill/oak-ridges',
  'peel/port-credit': 'mississauga/port-credit',
  'peel/lorne-park': 'mississauga/lorne-park',
  'peel/streetsville': 'mississauga/streetsville',
  'peel/erin-mills': 'mississauga/erin-mills',
  'peel/meadowvale': 'mississauga/meadowvale',
  'peel/bramalea': 'brampton/bramalea',
  'peel/heart-lake': 'brampton/heart-lake',
  'peel/springdale': 'brampton/springdale',
  'peel/bolton': 'caledon/bolton',
  'peel/caledon-village': 'caledon/caledon-village',
  'peel/palgrave': 'caledon/palgrave',
  'halton/old-oakville': 'oakville/old-oakville',
  'halton/bronte': 'oakville/bronte',
  'halton/glen-abbey': 'oakville/glen-abbey',
  'halton/aldershot': 'burlington/aldershot',
  'halton/brant-hills': 'burlington/brant-hills',
  'thornhill/bayview-glen': 'markham/bayview-glen',
  'durham/bay-ridges': 'pickering/bay-ridges',
  'durham/dunbarton': 'pickering/dunbarton',
  'durham/liverpool': 'pickering/liverpool',
  'durham/pickering-village': 'ajax/pickering-village',
  'durham/brooklin': 'whitby/brooklin',
  'durham/pringle-creek': 'whitby/pringle-creek',
  'durham/williamsburg': 'whitby/williamsburg',
  'durham/northglen': 'oshawa/northglen',
  'durham/samac': 'oshawa/samac',
  'cottage-country/picton': 'prince-edward-county/picton',
  'cottage-country/wellington': 'prince-edward-county/wellington',
  'cottage-country/bloomfield': 'prince-edward-county/bloomfield',
  'cottage-country/bala': 'muskoka/bala',
  'cottage-country/gravenhurst': 'muskoka/gravenhurst',
}

const primaryRoutes = PRIMARY_NEIGHBOURHOOD_KEYS.map((key): NeighbourhoodRoute => {
  const [city, neighbourhood] = key.split('/')
  return { key, city, neighbourhood, href: `/service-areas/${key}` }
})
const primaryByKey = new Map(primaryRoutes.map(route => [route.key, route]))

export function getPrimaryNeighbourhoodRoutes(): readonly NeighbourhoodRoute[] {
  return primaryRoutes
}

export function resolveNeighbourhoodRoute(city: string, neighbourhood: string): { kind: 'primary' | 'alias'; primary: NeighbourhoodRoute } | undefined {
  const key = `${city}/${neighbourhood}`
  const primary = primaryByKey.get(key)
  if (primary) return { kind: 'primary', primary }
  const target = Object.hasOwn(SERVICE_AREA_ALIASES, key) ? SERVICE_AREA_ALIASES[key] : undefined
  const destination = target ? primaryByKey.get(target) : undefined
  return destination ? { kind: 'alias', primary: destination } : undefined
}

/** Undefined means display plain text, never manufacture a route to unwritten content. */
export function getNeighbourhoodHref(city: string, neighbourhood: string): string | undefined {
  return resolveNeighbourhoodRoute(city, neighbourhood)?.primary.href
}

export function validateServiceAreaRoutes(
  primaries: readonly string[] = PRIMARY_NEIGHBOURHOOD_KEYS,
  aliases: Readonly<Record<string, string>> = SERVICE_AREA_ALIASES,
): string[] {
  const errors: string[] = []
  const primaryKeys = new Set(primaries)
  if (primaryKeys.size !== primaries.length) errors.push('Duplicate primary identity')
  for (const [source, target] of Object.entries(aliases)) {
    if (source === target) errors.push(`Self redirect: ${source}`)
    if (primaryKeys.has(source)) errors.push(`Alias is also primary: ${source}`)
    if (Object.hasOwn(aliases, target)) errors.push(`Redirect chain or cycle: ${source} -> ${target}`)
    if (!primaryKeys.has(target)) errors.push(`Missing primary target: ${target}`)
  }
  return errors
}
